import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'

import { useManagementGetServerDetails } from '@apps-common/apis/management/server-details'
import { NoResultsCard } from '@apps-common/components/common'
import { ButtonRoundSm, ButtonRoundedSm } from '@apps-common/components/elements/button'
import { ModalBody, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { IServerDetailModel } from '@apps-common/types/management/server-details'

export interface IServerDetailsSelectProps {
  subtitle: string
  title: string
}

export const ServerDetailsSelectModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { subtitle, title } = componentProps

  const [server, setServer] = useState<IServerDetailModel | null>(null)

  const {
    data: serverDetails,
    isLoading: serverDetailsIsLoading,
    isFetching: serverDetailsIsFetching,
    isRefetching: serverDetailsIsRefetching,
    isError: serverDetailsIsError
  } = useManagementGetServerDetails({})

  const loading = serverDetailsIsLoading || serverDetailsIsFetching || serverDetailsIsRefetching || serverDetailsIsError

  return (
    <ModalContainer alwaysMobile>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={subtitle} title={title} />
          <div className='flex items-center justify-end gap-[15px]'>
            <ButtonRoundSm type='button' className='bg-secondary-content/5 p-2' callback={() => onDismiss(null, ModalDismiss.Cancel)}>
              <commonIcons.close className='text-[1.25rem] text-secondary-content' />
            </ButtonRoundSm>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[90px]'>
        <div className='grid gap-[15px]'>
          <AnimatePresence mode='wait'>
            {(loading || !serverDetails) &&
              Array.from(Array(8).keys()).map((d) => (
                <motion.div key={`server-details-modal-skeleton-item-${d}`} {...motionFadeIn(0.2)}>
                  <div className='text-left bg-secondary-content/5 text-secondary-content rounded-3xl p-4 animate-pulse'>
                    <div className='flex items-center justify-between gap-2 gap-x-4'>
                      <div className='flex flex-col gap-[10px] grow'>
                        <div className='flex w-full'>
                          <div className='bg-secondary-content/5 px-3 py-1 rounded-xl w-[100px] h-[24px]' />
                        </div>
                        <div className='w-full h-[30px] bg-secondary-content/5 rounded-xl' />
                      </div>
                      <div className='bg-secondary-content/5 rounded-full min-w-[16px] min-h-[16px] flex items-center justify-center' />
                    </div>
                  </div>
                </motion.div>
              ))}
            {!loading &&
              serverDetails &&
              serverDetails.length > 0 &&
              serverDetails.map((d, i) => (
                <motion.div key={`server-details-modal-item-${d.id}-${i}`} {...motionFadeIn(0.2)}>
                  <button
                    type='button'
                    className='text-left bg-secondary-content/5 text-secondary-content rounded-3xl p-4'
                    onClick={() => setServer(d)}
                  >
                    <div className='flex items-center justify-between gap-2 gap-x-[10%]'>
                      <div className='flex flex-col gap-[10px]'>
                        <div className='flex'>
                          <div className='bg-secondary-content/10 px-3 py-1 rounded-xl'>{d.name}</div>
                        </div>
                        <div className='text-[0.8em]'>{d.description}</div>
                      </div>
                      <div className='border-[1px] border-secondary-content rounded-full min-w-[16px] min-h-[16px] flex items-center justify-center'>
                        {server?.id === d.id && <div className='bg-secondary-content rounded-full w-[12px] h-[12px]' />}
                      </div>
                    </div>
                  </button>
                </motion.div>
              ))}
            {!loading && serverDetails && serverDetails.length === 0 && (
              <motion.div key='server-details-modal-no-results-card' {...motionFadeIn(0.2)}>
                <NoResultsCard text='No server details found.' classes='bg-secondary-content/5 py-4' />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-[10px]'>
          <ButtonRoundedSm
            type='button'
            loading={loading || !server}
            text='Done'
            callback={() => onDismiss(JSON.stringify(server), ModalDismiss.Confirm)}
          />
          <ButtonRoundedSm type='button' text='Cancel' loading={false} callback={() => onDismiss(null, 'cancel')} />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
